/**
 * Module-scope coordination between the IDA entry point click, draft assistant
 * handlers, and queryResponseReceived.
 *
 * Tracks whether an IDA-initiated BizChat query is in flight, so that
 * queryResponseReceived can fire a 'BizChatResponseComplete' editor event
 * when the agent finishes streaming without writing content. Covers two cases:
 *   1. Agent calls get_draft but not rewrite_draft (asking for details)
 *   2. Agent never calls get_draft at all (e.g., no context prompt → chat-only response)
 *
 * Keyed by targetWindow (WeakMap) so concurrent compose sessions (e.g. multiple
 * popout windows) don't interfere. Each compose window has one BizChat session,
 * and all handler call sites (mark, clear, consume) receive targetWindow.
 * WeakMap also prevents memory leaks when popout windows close.
 *
 * A parallel Map<WindowKey, Window> allows the drawer-close orchestrator to
 * resolve Window from WindowKey for cancellation.
 *
 * Not in a Satchel store because this state does not drive UI rendering —
 * it only coordinates event flow between handler functions in this package.
 * (See wiki: "Do Not Use Satchel Store as Storage")
 */

import type { WindowKey } from "owa-bizchat-types";
import { getBizChatWindowKey } from "owa-copilot-chat-drawer-store";

/**
 * Default watchdog timeout. If no terminal BizChat status event (success,
 * error, or abort) arrives within this window after the user clicks the
 * IDA entry point, we treat the request as a silent empty-response
 * failure and recover via the onTimeout callback.
 */
export const DEFAULT_PENDING_DRAFT_TIMEOUT_MS = 60000;

interface PendingDraftRequest {
    editorId: string;
    cmmId: string | undefined;
    timeoutHandle: ReturnType<typeof setTimeout> | undefined;
}

const pendingByWindow = new WeakMap<Window, PendingDraftRequest>();
const windowByKey = new Map<WindowKey, Window>();

interface MarkPendingDraftRequestOptions {
    /**
     * Fired if no terminal status event arrives within `timeoutMs`. The
     * callback owns telemetry + editor-event side effects so this util
     * stays side-effect free and easy to unit test.
     */
    onTimeout?: () => void;
    /** Override the default watchdog window (in ms). */
    timeoutMs?: number;
    /**
     * Scenario CMM (form) id for the surface that initiated the request,
     * surfaced on milestone telemetry alongside `editorId` so IDA rows are
     * self-describing (scenario type + compose-form instance).
     */
    cmmId?: string;
}

/**
 * Called when the user clicks the IDA entry point and a BizChat query
 * will be auto-executed. Records the editor so queryResponseReceived
 * can fire the editor event if the agent responds without writing.
 *
 * If `onTimeout` is supplied, a watchdog timer is armed. The timer is
 * cleared by any of `clearPendingDraftRequest`, `consumePendingDraftRequest`,
 * or `consumePendingDraftRequestByWindowKey`. If it fires, the pending
 * entry is removed and the callback is invoked.
 */
export function markPendingDraftRequest(editorId: string, targetWindow: Window, options?: MarkPendingDraftRequestOptions): void {
    // Ensure any previous pending entry for this window is fully cleaned up
    // (timer included) before we overwrite it.
    clearPendingDraftRequest(targetWindow);
    
    const timeoutMs = options?.timeoutMs ?? DEFAULT_PENDING_DRAFT_TIMEOUT_MS;
    const onTimeout = options?.onTimeout;
    
    let timeoutHandle: ReturnType<typeof setTimeout> | undefined;
    if (onTimeout) {
        timeoutHandle = setTimeout(() => {
            // Remove the entry first so the callback can safely re-enter
            // the util (e.g. via callEditorApi → editor event → handler) without
            // re-triggering this timer.
            const entry = pendingByWindow.get(targetWindow);
            if (!entry) {
                return;
            }
            pendingByWindow.delete(targetWindow);
            windowByKey.delete(getBizChatWindowKey(targetWindow));
            onTimeout();
        }, timeoutMs);
    }
    
    pendingByWindow.set(targetWindow, { editorId, cmmId: options?.cmmId, timeoutHandle });
    windowByKey.set(getBizChatWindowKey(targetWindow), targetWindow);
}

/**
 * Called when rewrite_draft succeeds. Clears the pending flag for the
 * given window because the agent wrote content — no "asking" transition needed.
 * Also cancels the watchdog timer if one was armed.
 */
export function clearPendingDraftRequest(targetWindow: Window): void {
    const entry = pendingByWindow.get(targetWindow);
    if (entry?.timeoutHandle !== undefined) {
        clearTimeout(entry.timeoutHandle);
    }
    pendingByWindow.delete(targetWindow);
    windowByKey.delete(getBizChatWindowKey(targetWindow));
}

/**
 * Non-consuming lookup. Returns the editorId of the in-flight IDA draft
 * request for the given window without clearing the entry or watchdog timer.
 * Used by milestone-enrichment subscribers (e.g. the calendar IDA FTR
 * tagger) that need to read the editor session id at telemetry time
 * without ending the request.
 */
export function peekPendingDraftRequest(targetWindow: Window): {
    editorId: string;
    cmmId: string | undefined;
} | undefined {
    const entry = pendingByWindow.get(targetWindow);
    return entry ? { editorId: entry.editorId, cmmId: entry.cmmId } : undefined;
}

/**
 * Called by queryResponseReceived. Returns the pending entry for the given
 * window if one exists, then removes it (one-shot). Returns undefined if
 * no pending request exists for that window. Cancels the watchdog timer.
 */
export function consumePendingDraftRequest(targetWindow: Window): {
    editorId: string;
} | undefined {
    const entry = pendingByWindow.get(targetWindow);
    if (entry) {
        if (entry.timeoutHandle !== undefined) {
            clearTimeout(entry.timeoutHandle);
        }
        pendingByWindow.delete(targetWindow);
        windowByKey.delete(getBizChatWindowKey(targetWindow));
        return { editorId: entry.editorId };
    }
    return undefined;
}

/**
 * Called by the calendar IDA drawer-close orchestrator. Resolves a WindowKey to
 * the pending entry's editorId + cmmId, clears it, and returns them. Returns
 * undefined if no pending request exists for that key. Cancels the watchdog
 * timer.
 */
export function consumePendingDraftRequestByWindowKey(windowKey: WindowKey): {
    editorId: string;
    cmmId: string | undefined;
} | undefined {
    const targetWindow = windowByKey.get(windowKey);
    if (!targetWindow) {
        return undefined;
    }
    const entry = pendingByWindow.get(targetWindow);
    if (entry) {
        if (entry.timeoutHandle !== undefined) {
            clearTimeout(entry.timeoutHandle);
        }
        pendingByWindow.delete(targetWindow);
        windowByKey.delete(windowKey);
        return { editorId: entry.editorId, cmmId: entry.cmmId };
    }
    return undefined;
}
